import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import type { CaseDrop } from '@/components/admin/sessions/review/review-case';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { ReviewHistory } from '@/components/admin/sessions/review/review-history';
import { ReviewNotes } from '@/components/admin/sessions/review/review-notes';
import { ReviewNoyau } from '@/components/admin/sessions/review/review-noyau';
import { ReviewRangement } from '@/components/admin/sessions/review/review-rangement';
import { cn } from '@/lib/utils';
import type { ReviewEvent } from '@matter_extractor/shared';
import { type KeyboardEvent, useId } from 'react';

/**
 * The panel at the right of the transcript, in FOUR tabs (relecture.md §4.4,
 * D29): Rangement, Noyau, Notes, Historique, each with its count.
 *
 * Sticky beside the transcript from 768 px up, and under it below - everything
 * is read and written there, one ranges by the selection and the list (§4.4).
 * It stays mounted when the review is folded: a hidden panel keeps its tab and
 * the note being typed.
 *
 * The tabs are a real `tablist` - they switch the panel in place and write
 * nothing in the address, unlike the destination tabs of the parcours - and
 * the arrows walk them.
 */

export type ReviewTab = 'rangement' | 'noyau' | 'notes' | 'historique';

const TABS: readonly { readonly id: ReviewTab; readonly label: string }[] = [
  { id: 'rangement', label: 'Rangement' },
  { id: 'noyau', label: 'Noyau' },
  { id: 'notes', label: 'Notes' },
  { id: 'historique', label: 'Historique' },
];

export interface ReviewPanelProps {
  readonly tab: ReviewTab;
  readonly onTab: (tab: ReviewTab) => void;
  readonly folded: boolean;
  readonly protocolName: string | null;
  readonly protocolLabel: string | null;
  readonly flashCase: string | null;
  readonly drop: CaseDrop;
  readonly events: readonly ReviewEvent[] | undefined;
  readonly eventsFailed: boolean;
}

export function ReviewPanel({
  tab,
  onTab,
  folded,
  protocolName,
  protocolLabel,
  flashCase,
  drop,
  events,
  eventsFailed,
}: ReviewPanelProps) {
  const { review, grid } = useReviewApi();
  const base = useId();

  const counts: Record<ReviewTab, string> = {
    rangement: grid === null ? '-' : String(review.extracts.length),
    noyau: review.noyau?.noyau ?? (review.noyau?.unreadable === true ? '-' : ''),
    notes: String(review.notes.length),
    historique: events === undefined ? '' : String(events.length),
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const at = TABS.findIndex((entry) => entry.id === tab);
    const next = TABS[(at + (event.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length];
    if (next === undefined) return;
    event.preventDefault();
    onTab(next.id);
    document.getElementById(`${base}-tab-${next.id}`)?.focus();
  };

  return (
    <aside
      aria-label="Relecture"
      data-testid={REVIEW_TEST_IDS.panel}
      hidden={folded}
      className="flex min-w-0 flex-col gap-3 md:sticky md:top-[calc(var(--spacing-admin-bar)+0.75rem)] md:max-h-[calc(100vh-var(--spacing-admin-bar)-1.5rem)] md:overflow-y-auto md:pr-0.5"
    >
      <div
        role="tablist"
        aria-label="Relecture"
        onKeyDown={onKeyDown}
        className="flex flex-wrap gap-1 border-gray-200 border-b"
      >
        {TABS.map((entry) => {
          const selected = entry.id === tab;
          return (
            <button
              key={entry.id}
              id={`${base}-tab-${entry.id}`}
              type="button"
              role="tab"
              data-testid={REVIEW_TEST_IDS.tab}
              data-tab={entry.id}
              aria-selected={selected}
              aria-controls={`${base}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onTab(entry.id)}
              className={cn(
                '-mb-px cursor-pointer border-transparent border-b-[3px] px-3 py-1.5 font-semibold text-gray-600 text-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
                selected && 'border-bleu-nuit text-bleu-nuit',
              )}
            >
              {entry.label}
              {counts[entry.id] === '' ? null : (
                <span className="ml-1 font-normal text-gray-500">{counts[entry.id]}</span>
              )}
            </button>
          );
        })}
      </div>

      <div
        id={`${base}-panel`}
        role="tabpanel"
        data-testid={REVIEW_TEST_IDS.tabPanel}
        data-tab={tab}
        aria-labelledby={`${base}-tab-${tab}`}
      >
        {tab === 'rangement' ? (
          <ReviewRangement
            protocolName={protocolName}
            protocolLabel={protocolLabel}
            flashCase={flashCase}
            drop={drop}
          />
        ) : tab === 'noyau' ? (
          <ReviewNoyau />
        ) : tab === 'notes' ? (
          <ReviewNotes />
        ) : (
          <ReviewHistory events={events} failed={eventsFailed} />
        )}
      </div>
    </aside>
  );
}
